export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative bg-white py-28 md:py-36"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-x-8 gap-y-12">
                    <div className="col-span-12 lg:col-span-5 reveal">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            About Kairos ORD LLC
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                            One idea.
                            <br />
                            A lot of places
                            <br />
                            it can work.
                        </h2>

                        <div className="mt-12 hidden lg:block">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                                Why now
                            </div>
                            <ul className="mt-4 space-y-3">
                                <WhyItem text="Operations teams are ditching radios and paper logs for connected devices that route the right alert to the right person." />
                                <WhyItem text="Labs, warehouses, and manufacturing floors face rising demand for audit-grade event logs on refills, work orders, and safety events." />
                                <WhyItem text="Community-wellness budgets are expanding access to over-the-counter emergency supplies — distribution, not the supplies, is the gap." />
                                <WhyItem text="Standing-order and Good Samaritan laws are active in all 50 states." />
                            </ul>
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-7 lg:pl-6 reveal space-y-6 text-lg text-slate-600 leading-relaxed font-medium">
                        <p>
                            <span className="text-[#0A1428] font-bold">
                                Kairos
                            </span>{" "}
                            is the Greek word for the right moment — the
                            critical instant when action changes outcome.
                            Every day, operators, technicians, and communities
                            face those moments — a tank running low, a piece
                            of equipment down, a safety supply that needs to
                            be reached — and too often the answer is a radio
                            call, a group text, or a paper log.
                        </p>
                        <p>
                            Kairos ORD LLC builds a better answer. We design
                            connected{" "}
                            <span className="text-[#0A1428] font-bold">
                                alert and workflow-automation devices
                            </span>{" "}
                            that turn a single press into the right message,
                            to the right person, at the right time — with an
                            audit-grade event log behind it. Our platform
                            spans two categories: <span className="text-[#0A1428] font-bold">Alert &amp; Automation</span> for
                            labs, warehouses, and industrial facilities, and{" "}
                            <span className="text-[#0A1428] font-bold">
                                Public Safety
                            </span>{" "}
                            for community and venue programs that need
                            emergency supplies to be visible and accessible.
                        </p>
                        <p>
                            Kairos devices are already deployed in{" "}
                            <span className="text-[#0A1428] font-bold">
                                aerospace facilities and nightlife venues
                            </span>
                            , with new installations rolling out across labs,
                            warehouses, and community programs. From LN₂
                            scale monitoring and equipment work-order alerts,
                            to automated weight monitoring, drink-spike
                            detection, and rapid access to over-the-counter
                            Naloxone — the underlying idea is the same:{" "}
                            <span className="text-[#0A1428] font-bold">
                                there should be a button for that
                            </span>
                            . Kairos builds the system around the problem.
                        </p>
                        <p>
                            Founded by operators with backgrounds in{" "}
                            <span className="text-[#0A1428] font-bold">
                                aerospace, real estate, and business
                            </span>
                            , Kairos ORD is built by a team combining
                            aerospace engineering, manufacturing expertise,
                            and enterprise sales — every deployed unit
                            treated as an instrument so each next site is
                            smarter, faster, and better fit than the last.
                        </p>

                        {/* mobile Why Now */}
                        <div className="lg:hidden pt-6 border-t border-slate-200">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] font-bold text-slate-500">
                                Why now
                            </div>
                            <ul className="mt-4 space-y-3">
                                <WhyItem text="Operations teams are moving from radios and paper logs to connected devices that route alerts automatically." />
                                <WhyItem text="Labs and industrial facilities need audit-grade event logs on refills, work orders, and safety events." />
                                <WhyItem text="Community-wellness budgets are expanding access to OTC emergency supplies." />
                                <WhyItem text="Standing-order laws are active in all 50 states." />
                            </ul>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-6 border-t border-slate-200">
                            <Pill label="Mission" value="Emergency alert & workflow automation" />
                            <Pill label="Stage" value="Deployed · Aerospace & Nightlife" />
                            <Pill label="HQ" value="United States" />
                        </div>
                    </div>
                </div>

                {/* Leadership */}
                <div
                    data-testid="about-leadership"
                    className="mt-24 md:mt-28 pt-16 md:pt-20 border-t border-slate-200"
                >
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Leadership
                        </span>
                        <h3 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                            The team behind the button.
                        </h3>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <LeaderCard
                            testid="leader-logan-massett"
                            name="Logan Massett"
                            role="Founder & Chief Executive Officer"
                            bio="Founder of Kairos ORD and the driving force behind the company's vision, strategic partnerships, fundraising efforts, and commercialization strategy. Logan leads the development and deployment of Kairos alert, monitoring, and workflow-automation systems across industrial and public-safety environments."
                        />
                        <LeaderCard
                            testid="leader-william-gray"
                            name="William Gray"
                            role="Co-Founder & Chief Operating Officer"
                            bio="Co-Founder of Kairos ORD responsible for operational planning, deployment strategy, manufacturing coordination, and day-to-day business operations."
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const LeaderCard = ({ testid, name, role, bio }) => (
    <div
        data-testid={testid}
        className="reveal group bg-white border border-slate-200 p-8 md:p-10 hover:border-[#F0493E] transition-colors"
    >
        <div className="flex items-center gap-4">
            <div className="h-14 w-14 grid place-items-center border border-[#0A1428] group-hover:border-[#F0493E] transition-colors">
                <span className="font-display font-extrabold text-lg text-[#0A1428] tracking-tight">
                    {name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                </span>
            </div>
            <div>
                <div className="font-display font-extrabold text-xl md:text-2xl text-[#0A1428] tracking-tight leading-tight">
                    {name}
                </div>
                <div className="mt-1 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.22em] text-[#F0493E]">
                    {role}
                </div>
            </div>
        </div>
        <p className="mt-6 text-sm md:text-[15px] text-slate-600 leading-relaxed font-medium">
            {bio}
        </p>
    </div>
);

const WhyItem = ({ text }) => (
    <li className="flex items-start gap-3 text-sm md:text-[15px] text-slate-600 leading-relaxed font-medium">
        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F0493E] shrink-0" />
        <span>{text}</span>
    </li>
);

const Pill = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">
            {label}
        </div>
        <div className="mt-2 font-display font-bold text-[#0A1428] text-base">
            {value}
        </div>
    </div>
);
